import {
  Component,
  ComponentRef,
  EventEmitter,
  input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  reflectComponentType,
  SimpleChanges,
  viewChild,
} from '@angular/core';
import { Subject } from 'rxjs';
import { ComponentHostDirective } from '@core/directives/component-host.directive';
import { DoodProductModel, TreeCategoryWithProduct } from '@core/models/product.model';
import { takeUntil, tap } from 'rxjs/operators';
import { IContentBuilderFieldColor } from '@core/models/content-builder-fields.model';
import { ColorFieldTypesValues } from '@config/values/color-field-types.values';
import { ContentBuilderFieldColors } from '@shared/interfaces/content-builder.interface';

@Component({
  selector: 'app-category-factory',
  templateUrl: './category-factory.component.html',
})
export class CategoryFactoryComponent implements OnInit, OnDestroy, OnChanges {
  readonly componentClass = input<any>();
  readonly isOrderingAllowed = input(true);
  readonly categoryNameColor = input<IContentBuilderFieldColor>({
    value: 'neutral-900',
    type: ColorFieldTypesValues.Palette,
  });
  readonly categoryDescriptionColor = input<IContentBuilderFieldColor>({
    value: 'neutral-600',
    type: ColorFieldTypesValues.Palette,
  });
  readonly highlightCategoryNameColor = input<IContentBuilderFieldColor>({
    type: ColorFieldTypesValues.Default,
    value: '',
  });
  readonly highlightCategoryBackgroundColor = input<IContentBuilderFieldColor>({
    value: 'neutral-600',
    type: ColorFieldTypesValues.Palette,
  });
  readonly productNameColor = input<IContentBuilderFieldColor>({
    value: 'neutral-900',
    type: ColorFieldTypesValues.Palette,
  });
  readonly productDescriptionColor = input<IContentBuilderFieldColor>({
    value: 'neutral-900',
    type: ColorFieldTypesValues.Palette,
  });
  readonly productPriceColor = input<IContentBuilderFieldColor>({
    value: 'neutral-900',
    type: ColorFieldTypesValues.Palette,
  });
  readonly productImagePlaceholderColor = input<IContentBuilderFieldColor>({
    value: 'neutral-50',
    type: ColorFieldTypesValues.Palette,
  });
  readonly productBorderColor = input<IContentBuilderFieldColor>({
    value: 'neutral-100',
    type: ColorFieldTypesValues.Palette,
  });
  readonly productBackgroundColor = input<IContentBuilderFieldColor>({
    value: 'transparent',
    type: ColorFieldTypesValues.Hex,
  });
  readonly tagColors = input.required<ContentBuilderFieldColors>();

  readonly treeCategoryWithProduct = input.required<TreeCategoryWithProduct>();

  @Output() navigateTo = new EventEmitter<DoodProductModel>();

  readonly componentHost = viewChild(ComponentHostDirective);

  private destroyed$ = new Subject<boolean>();
  private componentRef?: ComponentRef<any>;

  ngOnChanges(_changes: SimpleChanges): void {
    if (this.componentRef) {
      let products = this.treeCategoryWithProduct().products;

      Object.assign(
        this.componentRef.instance,
        {},
        {
          category: products.length ? this.treeCategoryWithProduct().category : undefined,
          products: products,
          isOrderingAllowed: this.isOrderingAllowed(),
          level: this.treeCategoryWithProduct().level,
        },
      );
      this.setComponentRefInputs();
      this.safeSetInput('products', products);
    }
  }

  ngOnInit(): void {
    const componentHost = this.componentHost();
    componentHost?.viewContainerRef.clear();

    if (!componentHost) {
      return;
    }

    const componentClass = this.componentClass();
    if (!componentClass) {
      return;
    }

    const viewContainerRef = componentHost.viewContainerRef;
    viewContainerRef.clear();

    this.componentRef = viewContainerRef.createComponent<any>(componentClass);

    if (this.treeCategoryWithProduct().products.length) {
      this.setComponentRefInputs();
    }

    this.componentRef.instance.navigateTo
      .pipe(
        takeUntil(this.destroyed$),
        tap(($event: any) => this.navigateTo.emit($event)),
      )
      .subscribe();
  }

  private setComponentRefInputs() {
    const targetInputsName = reflectComponentType(this.componentClass())?.inputs.map(
      i => i.propName,
    );

    this.safeSetInput('category', this.treeCategoryWithProduct().category, targetInputsName);
    this.safeSetInput('products', this.treeCategoryWithProduct().products, targetInputsName);
    this.safeSetInput('isOrderingAllowed', this.isOrderingAllowed(), targetInputsName);
    this.safeSetInput('level', this.treeCategoryWithProduct().level, targetInputsName);
    this.safeSetInput('categoryNameColor', this.categoryNameColor(), targetInputsName);
    const categoryDescriptionColor = this.categoryDescriptionColor();
    this.safeSetInput('categoryDescriptionColor', categoryDescriptionColor, targetInputsName);
    this.safeSetInput(
      'highlightCategoryNameColor',
      this.highlightCategoryNameColor(),
      targetInputsName,
    );
    this.safeSetInput(
      'highlightCategoryBackgroundColor',
      this.highlightCategoryBackgroundColor(),
      targetInputsName,
    );
    this.safeSetInput('productNameColor', this.productNameColor(), targetInputsName);
    this.safeSetInput('productDescriptionColor', this.productDescriptionColor(), targetInputsName);
    this.safeSetInput('categoryDescriptionColor', categoryDescriptionColor, targetInputsName);
    this.safeSetInput('productPriceColor', this.productPriceColor(), targetInputsName);
    this.safeSetInput(
      'productImagePlaceholderColor',
      this.productImagePlaceholderColor(),
      targetInputsName,
    );
    this.safeSetInput('productBorderColor', this.productBorderColor(), targetInputsName);
    this.safeSetInput('productBackgroundColor', this.productBackgroundColor(), targetInputsName);
    this.safeSetInput('tagColors', this.tagColors(), targetInputsName);
  }

  private safeSetInput(inputName: string, value: any, targetInputsName?: string[]): void {
    if (this.componentRef && this.hasInput(inputName, targetInputsName ?? [])) {
      this.componentRef.setInput(inputName, value);
    }
  }

  private hasInput(inputName: string, componentsInputNames: string[]): boolean {
    if (!this.componentRef?.instance) {
      return false;
    }

    return componentsInputNames.includes(inputName);
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }
}
